import * as React from 'react';
import styled from 'styled-components';
import { useRefState } from '../../../utils/customHooks';
import CarIcon from '../../custom-icons/car';
import { PrimaryColor, SidelyBlack } from '../css/Utils';

const LoaderWrapper = styled.div<{ width: string }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: ${p => p.width};
	gap: 1ch;
`;

const LoaderMessage = styled.h4`
	color: white;
	margin: 0;
`;

const LoaderBg = styled.div<{ color: string, width: string, height: number }>`
	background: color-mix(in oklab, ${p => p.color} 30%, transparent);
	border-radius: 100px;
	width: 100%;
	height: ${p => p.height}px;
	padding: ${p => p.height / 10}px;
`;

const LoaderInside = styled.div<{ color: string, progress: number }>`
	background: ${p => p.color};
	border-radius: 100px;
	height: 100%;
	width: calc(100% * ${p => p.progress});
	transition: width 0.5s ease-in-out;
	position: relative;
`;

const CircularLoader = styled.div<{ color: string; progress: number, width: string }>`
	@property --progress {
		syntax: '<number>';
		inherits: false;
		initial-value: 0;
	}

	--progress: ${p => p.progress};

	width: ${p => p.width};
	aspect-ratio: 1;
	border-radius: 50%;

	background: conic-gradient(
		${p => p.color} calc(var(--progress) * 360deg),
		color-mix(in oklab, ${p => p.color} 20%, transparent) 0deg
	);

	transition: --progress 0.6s ease-in-out;
	display: grid;
	place-items: center;
	mask: radial-gradient(
		farthest-side,
		transparent 65%,
		${SidelyBlack} 66%
	);
	-webkit-mask: radial-gradient(
		farthest-side,
		transparent 65%,
		${SidelyBlack} 66%
	);
`;

const InnerProgressionDiv = styled.div`
	position: absolute;
	right: 10px;
	max-width: 100%;
	height: 100%;
	color: white;
	overflow: hidden;
	text-overflow: ellipsis;
	display: flex;
	align-items: center;
	font-size: 12px;
`;

type ProgressBarProps = {
	color?: string,
	width?: string,
	height?: number,
	circle?: boolean,
	progress?: number, // between 0/1 by default
	step?: number,
	maxProgression?: number,
	fake?: boolean,
	showPercentage?: boolean,
	message?: string,
	carIcon?: boolean
}

const DEFAULT_STEP = 0.3;

export default function ProgressBar(props: ProgressBarProps) {
	const [interval, setLocalInterval] = React.useState<NodeJS.Timer | null>(null);
	const [progressRef, setProgress] = useRefState<number>(props.progress ?? 0);
	const [visualProgress, setVisualProgress] = React.useState<number>(props.progress ?? 0);
	const [stepRef, setStep] = useRefState<number>(props.step ?? DEFAULT_STEP);
	const [maxProgressionRef, setMaxProgression] = useRefState(props.maxProgression);

	React.useEffect(() => {
		setMaxProgression(props.maxProgression);
		setStep(props.step ?? DEFAULT_STEP);
		if (props.fake) {
			if (interval) clearInterval(interval);
			setLocalInterval(setInterval(() => {
				setVisualProgress(() => {
					const currentProgress = progressRef.current + stepRef.current;
					setProgress(currentProgress);
					const visualProgress = Math.round(Math.atan(currentProgress) / (Math.PI / 2) * (maxProgressionRef.current ?? 1) * 100 * 1000) / 100000;
					if (visualProgress >= (maxProgressionRef.current ?? 1) || currentProgress >= (maxProgressionRef.current ?? 1)) {
						if (interval) clearInterval(interval);
					} else if (visualProgress >= 0.7 * (maxProgressionRef.current ?? 1) || currentProgress >= 0.7 * (maxProgressionRef.current ?? 1)) {
						setStep(0.1);
					}
					return visualProgress;
				});
			}, 100));
		} else if (interval) {
			clearInterval(interval);
		}
		return () => {
			if (interval) clearInterval(interval);
		};
	}, [props.fake, props.maxProgression]);

	const color = props.color ?? PrimaryColor;
	const width = props.width ?? '100%';
	const height = props.height ?? 15;
	const maxedVisualProgress = visualProgress > 1 ? 1 : visualProgress;
	let content: React.ReactNode;
	if (props.circle) {
		content = <CircularLoader color={color} progress={maxedVisualProgress} width={width} />;
	} else {
		content = <LoaderBg color={color} width={width} height={height}>
			{
				<LoaderInside color={color} progress={maxedVisualProgress}>
					{(props.carIcon || props.showPercentage) &&
						<InnerProgressionDiv>
							{props.showPercentage ? (visualProgress * 100).toFixed(2) + '%' : <CarIcon color='white' style={{ height: '100%' }} />}
						</InnerProgressionDiv>
					}
				</LoaderInside>

			}
		</LoaderBg>;
	}
	return <LoaderWrapper width={width}>
		{props.message && <LoaderMessage>{props.message}</LoaderMessage>}
		{content}
	</LoaderWrapper>;
}
